import { View } from '@play-co/timestep-core/ui';
import LangBitmapFontTextView from 'src/lib/ui/components/LangBitmapFontTextView';
import i18n from 'src/lib/i18n/i18n';
import BaseTeamPage from './BaseTeamPage';
import { AsyncScroll } from 'src/game/components/shared/AsyncScroll';
import StateObserver, { user } from 'src/StateObserver';
import { TeamMemberEntry } from './TeamMemberEntry';
import { withLoading } from 'src/lib/utils';
import { Avatar } from 'src/game/components/shared/Avatar';
import {
  MAX_MEMBERS,
  TeamMemberData,
  TeamRole,
} from 'src/replicant/team/team.ruleset';
import buttons from 'src/lib/ui/config/buttons';
import ButtonScaleViewWithText, {
  ButtonScaleViewWithTextOpts,
} from 'src/lib/ui/components/ButtonScaleViewWithText';
import { Feature } from 'src/replicant/analytics';
import {
  getCurrentTeamCompetition,
  getCurrentTeamProfile,
  getTeamChatMessages,
  getTeamMembers,
} from '../team.sequence';
import sounds from 'src/lib/sounds';
import { openPopupPromise } from 'src/lib/popups/popupOpenClose';
import { getUserSharedState } from 'src/replicant/config/actions/createSharedStatesActions';
import { track } from 'src/lib/analytics/utils';
import { ScrollTooltip } from 'src/game/components/tooltip/ScrollTooltip';
import { PlayerID } from 'src/replicant/friends/friends.ruleset';
import { ButtonsTooltip } from 'src/game/components/tooltip/ButtonTooltip';
import uiConfig from 'src/lib/ui/config';

export default class MyTeamsPage extends BaseTeamPage {
  private scroll!: AsyncScroll<TeamMemberData, TeamMemberEntry>;
  private tooltip?: ScrollTooltip<ButtonsTooltip>;
  private data: TeamMemberData[] = [];

  constructor(private superview: View) {
    super(superview);
    this.addContent();
    void this.addHeader();
  }

  private async addHeader() {
    // Remove previous header.
    this.header.removeAllSubviews();

    const profile = await withLoading(
      async () => getCurrentTeamProfile()?.get(),
      {
        superview: this.header,
        color: '#F24463',
        feature: Feature.TEAM._,
        subFeature: Feature.TEAM.GET_TEAM_PROFILE,
      },
    );

    new Avatar({
      superview: this.header,
      x: 55,
      y: this.header.style.height * 0.5,
      width: 70,
      height: 70,
      image: profile?.icon,
    });

    new LangBitmapFontTextView({
      superview: this.header,
      font: 'Body',
      size: 34,
      color: '#F24463',
      localeText: () => profile?.name ?? '',
      width: 200,
      height: 40,
      x: 195,
      y: this.header.style.height * 0.5 - 4,
      align: 'left',
      verticalAlign: 'center',
      centerOnOrigin: true,
    });

    new LangBitmapFontTextView({
      superview: this.header,
      x: 450,
      y: 15,
      align: 'center',
      verticalAlign: 'center',
      size: 24,
      font: 'Body',
      width: 90,
      height: 30,
      color: '#BF8147',
      localeText: () => i18n('match2.team.membersCount'),
    });

    new LangBitmapFontTextView({
      superview: this.header,
      x: 450,
      y: 45,
      align: 'left',
      verticalAlign: 'center',
      size: 28,
      font: 'Body',
      width: 90,
      height: 34,
      color: '#F24463',
      localeText: () => `${profile?.memberCount ?? 0}/${MAX_MEMBERS}`,
    });

    new ButtonScaleViewWithText({
      superview: this.header,
      ...buttons.secondary,
      fontSize: 40,
      labelOffsetY: -2,
      font: 'Body',
      width: 132,
      height: 73,
      x: 630,
      y: 50,
      localeText: () => i18n('match2.team.info'),
      onClick: async () => {
        sounds.sfx.play('ok');
        const result = await openPopupPromise('popupTeamInfo', {
          team: profile!,
        });

        if (result === 'leave') {
          this.emit('Close');
          void openPopupPromise('popupTeam', {});
        }
      },
      centerOnOrigin: true,
      tracking: {
        feature: Feature.MATCH2.TEAM._,
        $subFeature: Feature.MATCH2.TEAM.INFO,
      },
    });
  }

  private addContent() {
    this.scroll = new AsyncScroll({
      superview: this,
      rect: {
        x: 0,
        y: this.header.style.height,
        width: this.superview.style.width,
        height: this.superview.style.height - this.header.style.height,
      },
      createItem: (superview, index, data) => {
        const entry = new TeamMemberEntry({
          superview,
          onEntryClick: async () => {
            // Skip if no tooltip
            if (!this.tooltip) return;
            const data = this.data[index];

            /**
             * Skip if no data or clicking on own entry.
             * Also hide any unfocused tooltip.
             */
            if (!data || data.me) {
              this.tooltip.hide();
              return;
            }

            this.tooltip.showAtItem(index);

            // Update tooltip button label after tooltip index is set
            this.updateTooltipCTAButtonLabel();
          },
        });
        entry.on('Close', () => {
          this.emit('Close');
        });
        entry.style.x = (superview.style.width - entry.style.width) * 0.5;
        if ('loading' in data) return entry;
        entry.setup(data);
        return entry;
      },
      recycle: true,
      setData: (_, data, view) => {
        if ('loading' in data) return;
        view.setup(data);
      },
    });

    const buttonsConfig: ButtonScaleViewWithTextOpts[] = [
      {
        ...uiConfig.buttons.cancel,
        localeText: () => i18n('match2.team.context.block'),
        onClick: async () => {
          // Skip if no tooltip
          if (!this.tooltip) return;

          // Skip if no focused item
          if (this.tooltip.index === undefined) return;

          sounds.sfx.play('ok');

          const { id } = this.data[this.tooltip.index];
          const player = this.data.find((x) => x.me);
          const isAdmin = player?.profile.role === TeamRole.Admin;

          await withLoading(
            async () => {
              // If admin, perform kick action
              if (isAdmin) return this.onKick(id);
              // Otherwise, perform block/unblock action
              return this.onBlockToggle(id);
            },
            {
              feature: Feature.TEAM._,
              subFeature: isAdmin
                ? Feature.TEAM.KICK_MEMBER
                : Feature.TEAM.BLOCK_TOGGLE,
            },
          );

          // Hide tooltip after action
          await this.tooltip.hide();
        },
        tracking: {
          feature: Feature.TEAM._,
          $subFeature: Feature.TEAM.TOOLTIP_CTA,
        },
      },
    ];

    // Create a button tooltip
    const tooltip = new ButtonsTooltip({
      superview: this,
      tooltipOffsetY: 20,
      buttons: buttonsConfig,
    });

    // Create a wrapper scroll tooltip to handle triggering and positioning
    this.tooltip = new ScrollTooltip({
      scroll: this.scroll,
      tooltip,
      offsetY: 45,
    });

    this.scroll.setDataProvider(
      async () => {
        const members = await getTeamMembers()?.get();
        if (!members) return [];
        this.data = members;
        return members;
      },
      {
        feature: Feature.TEAM._,
        subFeature: Feature.TEAM.GET_TEAM_MEMBERS,
      },
    );
  }

  private updateTooltipCTAButtonLabel() {
    // Skip if no tooltip
    if (!this.tooltip) return;

    // Skip if no focused item
    const { index } = this.tooltip;
    if (index === undefined) return;

    // Skip if no data
    const data = this.data[index];
    if (!data) return;

    // Skip if no button. The CTA is the only button in the tooltip; it sat at
    // index 1 while newUI prepended a "View Profile" button ahead of it.
    const button = this.tooltip.getTooltip().getButton(0);
    if (!button) return;

    const sharedStates = getUserSharedState(user());
    const teamId = user().team.currentTeamId!;
    const player = this.data.find((x) => x.me);
    const isAdmin = player?.profile.role === TeamRole.Admin;
    const isBlocked = sharedStates.team[teamId]?.blockedUsers.includes(data.id);
    let text = 'match2.team.context.kick';

    // If not admin, change to block/unblock label
    if (!isAdmin) {
      text = isBlocked
        ? 'match2.team.context.unblock'
        : 'match2.team.context.block';
    }

    button.label.updateOpts({
      localeText: () => i18n(text),
    });
  }

  private async onKick(id: PlayerID) {
    const profile = await getCurrentTeamProfile()?.get();
    const competition = await getCurrentTeamCompetition()?.get();
    await StateObserver.invoke.asyncKickMember({ userId: id });
    await Promise.all([
      getCurrentTeamProfile()?.invalidate(),
      getTeamMembers()?.invalidate(),
      getTeamChatMessages()?.invalidate(),
    ]);
    this.addHeader();
    this.scroll.refresh();
    track('TeamKick', {
      id,
      score: competition?.myTeam?.find((x) => x.id === id)?.score ?? 0,
      memberCount: profile?.memberCount ?? 0,
      kickedId: id,
    });
  }

  private async onBlockToggle(id: PlayerID) {
    const teamId = user().team.currentTeamId!;
    const sharedStates = getUserSharedState(user());

    // If already blocked, unblock instead
    if (sharedStates.team[teamId]?.blockedUsers.includes(id)) {
      return StateObserver.invoke.unblockUser({ userId: id });
    }

    // Otherwise, block user
    return StateObserver.invoke.blockUser({ userId: id });
  }
}
